import { FetchResult } from "@apollo/client";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
} from "@mui/material";
import { ReactElement } from "react";
import { FormattedMessage } from "react-intl";

import {
  FormikDialog,
  FormikSelect,
  FormikSubmitButton,
} from "~frontend/components/forms";
import theme from "~frontend/theme";

type EscalateOption = { inboxName: string; inboxId: string };

export function EscalateDialog({
  isOpen,
  escalateOptions,
  handleEscalate,
  handleClose,
}: {
  isOpen: boolean;
  escalateOptions: EscalateOption[];
  handleEscalate: (inboxId: string) => Promise<FetchResult<unknown>>;
  handleClose: () => void;
}): ReactElement {
  return (
    <FormikDialog<{ escalationReason: string }>
      open={isOpen}
      initialValues={{
        escalationReason: escalateOptions[0].inboxId,
      }}
      onSubmit={values => {
        handleClose();
        return handleEscalate(values.escalationReason);
      }}
    >
      {() => (
        <>
          <DialogTitle>
            <FormattedMessage
              defaultMessage="Escalate this Conversation"
              description="Title for modal to escalate an assignment"
            />
          </DialogTitle>
          <DialogContent>
            <Typography>
              <FormattedMessage
                defaultMessage="Select your reason for escalation below, and this conversation will be moved to the appropriate inbox."
                description="Instructions for escalating an assignment"
              />
            </Typography>

            <Typography
              sx={{
                pt: theme.spacing(1),
                pb: theme.spacing(1),
              }}
            >
              <strong>
                <FormattedMessage
                  defaultMessage="Note:"
                  description="Callout warning"
                />{" "}
              </strong>
              <FormattedMessage
                defaultMessage="Once this conversation is escalated, only admins and escalation inbox members will be able to access it."
                description="Warning about losing access to an escalated conversation"
              />
            </Typography>

            <FormikSelect
              name="escalationReason"
              fullWidth
              label={
                <FormattedMessage
                  defaultMessage="Escalation Reason"
                  description="Escalation reason selector label"
                />
              }
            >
              {escalateOptions.map(({ inboxName, inboxId }) => (
                <MenuItem key={inboxId} value={inboxId}>
                  {inboxName}
                </MenuItem>
              ))}
            </FormikSelect>
          </DialogContent>
          <DialogActions sx={{ padding: theme.spacing(1) }}>
            <Button disabled={false} onClick={handleClose}>
              <FormattedMessage
                defaultMessage="Cancel"
                description="Cancel button"
              />
            </Button>
            <FormikSubmitButton
              disabled={false}
              variant="contained"
              submitText={
                <FormattedMessage
                  defaultMessage="Escalate This Conversation"
                  description="Submit button text for escalating an assignment"
                />
              }
            />
          </DialogActions>
        </>
      )}
    </FormikDialog>
  );
}
